import React, { useEffect, useState } from "react";
import { DownloadSimple, ChartBar, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const COLS = [
  ["name", "Student"], ["student_id", "ID"], ["grade", "Grade"], ["class_name", "Class"],
  ["school_name", "School"], ["course_title", "Course"], ["lessons_completed", "Lessons done"],
  ["progress_pct", "Progress %"], ["completed", "Completed"], ["completed_at", "Completed on"],
  ["activated", "Activated"], ["last_login_at", "Last login"],
];

export default function BoardReportsPanel({ user }) {
  const isSuper = user?.role === "super_admin";
  const [boards, setBoards] = useState([]);
  const [boardId, setBoardId] = useState(isSuper ? "" : user?.school_board_id || "");
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    if (isSuper) {
      api.get("/boards").then(({ data }) => {
        setBoards(data);
        if (data.length && !boardId) setBoardId(data[0].id);
      }).catch(() => {});
    }
    // eslint-disable-next-line
  }, []);

  const load = async (id) => {
    if (!id) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/boards/${id}/progress-report`);
      setReport(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  useEffect(() => { if (boardId) load(boardId); }, [boardId]); // eslint-disable-line

  const rows = (report?.rows || []).filter(r => {
    if (!filter.trim()) return true;
    const q = filter.toLowerCase();
    return [r.name, r.class_name, r.school_name, r.student_id].some(v => (v || "").toLowerCase().includes(q));
  });

  const exportCsv = () => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [COLS.map(([, h]) => esc(h)).join(",")]
      .concat(rows.map(r => COLS.map(([k]) => {
        let v = r[k];
        if (k === "completed" || k === "activated") v = v ? "yes" : "no";
        if ((k === "completed_at" || k === "last_login_at") && v) v = String(v).slice(0, 10);
        return esc(v);
      }).join(","))).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `progress-report-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const done = rows.filter(r => r.completed).length;
  const active = rows.filter(r => r.activated).length;

  return (
    <div className="card" data-testid="board-reports-panel">
      <h2 className="text-xl font-bold font-display mb-1 flex items-center gap-2">
        <ChartBar size={22} weight="fill" color="#CCFF00" className="icon-outline" /> Progress Reports
      </h2>
      <p className="text-sm text-[#000000] mb-4">Every student's progress, completions and last activity — export any time.</p>

      <div className="flex items-center gap-2 flex-wrap mb-4">
        {isSuper && (
          <select className="input-base max-w-xs" value={boardId} onChange={e => setBoardId(e.target.value)} data-testid="report-board-select">
            {boards.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        )}
        <input className="input-base max-w-xs" placeholder="Filter by name, class, school…" value={filter}
          onChange={e => setFilter(e.target.value)} data-testid="report-filter-input" />
        <button className="btn-secondary text-xs flex items-center gap-1" onClick={() => load(boardId)} disabled={busy} data-testid="report-refresh">
          <ArrowsClockwise size={13} weight="bold" /> Refresh
        </button>
        <button className="btn-primary text-xs flex items-center gap-1" onClick={exportCsv} disabled={!rows.length} data-testid="report-export-csv">
          <DownloadSimple size={13} weight="bold" /> Export CSV ({rows.length})
        </button>
      </div>

      {report && (
        <div className="flex items-center gap-4 text-sm mb-3 flex-wrap" data-testid="report-summary">
          <span><b>{rows.length}</b> students</span>
          <span><b>{active}</b> activated</span>
          <span><b>{done}</b> completed the course</span>
        </div>
      )}

      {busy && <div className="text-[#000000] text-sm">Loading…</div>}
      {!busy && report && rows.length === 0 && (
        <div className="text-[#000000] text-sm italic" data-testid="report-empty">
          No students yet — they appear here once classes have enrolled students.
        </div>
      )}
      {!busy && rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="report-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-[#000000] border-b border-[#C4C8CE]">
                <th className="py-2 pr-3">Student</th><th className="py-2 pr-3">Grade</th>
                <th className="py-2 pr-3">Class</th><th className="py-2 pr-3">School</th>
                <th className="py-2 pr-3">Progress</th><th className="py-2 pr-3">Completed</th>
                <th className="py-2 pr-3">Last login</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-b border-[#EDEEF0]" data-testid={`report-row-${i}`}>
                  <td className="py-2 pr-3 font-semibold">{r.name}{r.student_id && <span className="text-xs text-[#94979C] font-normal"> · {r.student_id}</span>}</td>
                  <td className="py-2 pr-3">{r.grade || "—"}</td>
                  <td className="py-2 pr-3">{r.class_name || "—"}</td>
                  <td className="py-2 pr-3">{r.school_name || "—"}</td>
                  <td className="py-2 pr-3">
                    <div className="flex items-center gap-2">
                      <div className="w-20 h-1.5 rounded-full bg-[#EDEEF0] overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${r.progress_pct}%`, background: r.progress_pct >= 100 ? "#CCFF00" : "#A3B800" }} />
                      </div>
                      <span className="text-xs">{r.progress_pct}%</span>
                    </div>
                  </td>
                  <td className="py-2 pr-3">{r.completed ? <span className="text-[#3F6212] font-bold">✓ {String(r.completed_at || "").slice(0, 10)}</span> : "—"}</td>
                  <td className="py-2 pr-3 text-xs">{r.last_login_at ? String(r.last_login_at).slice(0, 10) : (r.activated ? "activated" : "never")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
